import { useState, useEffect } from "react";
import { doc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
import { signInWithPopup, GoogleAuthProvider, onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../firebase";

// ─────────────────────────────────────────────────────────────────────────────
// RESOLUCIÓN DE CÓDIGOS QR — /q/{id}
//
// Los QR ploteados en los equipos apuntan acá, al dominio propio, y no al
// dominio de Netlify de cada app. Razón: un sticker pegado en una máquina no se
// puede corregir con un deploy — hay que ir hasta el equipo y despegarlo. Si la
// plataforma migra de hosting o una app cambia de dominio, esta página redirige
// al lugar nuevo y los 124 stickers siguen sirviendo.
//
// La URL de destino se lee de `apps/{appId}.url` en Firestore, la misma fuente
// que usa el launcher. Cambiarla ahí alcanza para redirigir todos los QR.
//
// Cuatro situaciones a resolver:
//   sin sesión              → login, y después sigue al equipo
//   con sesión y acceso     → redirige a la ficha, con SSO
//   con sesión sin acceso   → mensaje claro, no un error genérico
//   equipo inexistente      → "no encontrado"
//
// Un equipo de OTRO tenant devuelve el mismo "no encontrado" que uno que no
// existe: decir "existe pero no es tuyo" filtraría información entre clientes.
// ─────────────────────────────────────────────────────────────────────────────

const FONT = "'Plus Jakarta Sans', sans-serif";
const APP_EQUIPOS = "equipos";

function Isotipo({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40">
      <line x1="20" y1="5" x2="20" y2="20" stroke="#D1D5DB" strokeWidth="2" />
      <line x1="35" y1="20" x2="20" y2="20" stroke="#D1D5DB" strokeWidth="2" />
      <line x1="20" y1="35" x2="20" y2="20" stroke="#D1D5DB" strokeWidth="2" />
      <line x1="5" y1="20" x2="20" y2="20" stroke="#D1D5DB" strokeWidth="2" />
      <circle cx="20" cy="5" r="3" fill="#E03131" />
      <circle cx="35" cy="20" r="3" fill="#E03131" />
      <circle cx="20" cy="35" r="3" fill="#E03131" />
      <circle cx="5" cy="20" r="3" fill="#E03131" />
      <circle cx="20" cy="20" r="6" fill="#E03131" />
    </svg>
  );
}

/** El id sale del path /q/{id} */
function idDeLaUrl() {
  const m = window.location.pathname.match(/^\/q\/([A-Za-z0-9_-]+)/);
  return m ? m[1] : null;
}

export default function QrRedirectPage() {
  const [estado, setEstado] = useState("cargando");
  // cargando | login | resolviendo | redirigiendo | no_encontrado | sin_acceso | error
  const [equipo, setEquipo] = useState(null);
  const [destino, setDestino] = useState(null);
  const [error, setError] = useState("");

  const id = idDeLaUrl();

  useEffect(() => {
    if (!id) { setEstado("no_encontrado"); return; }

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { setEstado("login"); return; }
      setEstado("resolviendo");
      try {
        // 1. Perfil del usuario, para saber su tenant
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (!userDoc.exists()) { setEstado("sin_acceso"); return; }
        const perfil = userDoc.data();

        // 2. El equipo. Si no existe o es de otro tenant → mismo mensaje
        const eqDoc = await getDoc(doc(db, "equipos", id));
        if (!eqDoc.exists() || eqDoc.data().tenant_id !== perfil.tenant_id) {
          setEstado("no_encontrado");
          return;
        }
        const eq = { id: eqDoc.id, ...eqDoc.data() };
        setEquipo(eq);

        // 3. ¿Tiene acceso a la app de Equipos? El superadmin siempre.
        if (perfil.role_platform !== "superadmin") {
          const rolesSnap = await getDocs(query(
            collection(db, "user_app_roles"),
            where("user_id", "==", user.uid),
            where("tenant_id", "==", perfil.tenant_id),
            where("app_id", "==", APP_EQUIPOS),
            where("enabled", "==", true),
          ));
          if (rolesSnap.empty) { setEstado("sin_acceso"); return; }
        }

        // 4. URL de la app desde Firestore (misma fuente que el launcher)
        const appDoc = await getDoc(doc(db, "apps", APP_EQUIPOS));
        const base = appDoc.exists() ? appDoc.data().url : null;
        if (!base) { setError("La app de Equipos no está configurada."); setEstado("error"); return; }

        const sep = base.includes("?") ? "&" : "?";
        const url = `${base.replace(/\/$/, "")}/equipos/${id}${sep}sso=1&email=${encodeURIComponent(user.email)}`;
        setDestino(url);
        setEstado("redirigiendo");
        // Un respiro para que se vea qué equipo es antes de saltar
        setTimeout(() => { window.location.replace(url); }, 700);
      } catch (e) {
        console.error("QR redirect:", e);
        setError("No se pudo resolver el código.");
        setEstado("error");
      }
    });
    return unsub;
  }, [id]);

  async function entrar() {
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      // onAuthStateChanged retoma el flujo solo
    } catch (e) {
      if (e.code !== "auth/popup-closed-by-user") setError("No se pudo iniciar sesión.");
    }
  }

  const nombre = equipo
    ? [equipo.descripcion_tipo, equipo.marca].filter(Boolean).join(" ")
    : "";

  return (
    <div style={sx.container}>
      <div style={sx.card}>
        <div style={sx.logoArea}>
          <Isotipo size={36} />
          <div style={sx.logoText}>
            <span style={{ color: "#0B0F14", fontWeight: 500 }}>Obra</span>
            <span style={{ color: "#E03131", fontWeight: 700 }}>Core</span>
          </div>
        </div>

        {(estado === "cargando" || estado === "resolviendo") && (
          <p style={sx.text}>Buscando el equipo...</p>
        )}

        {estado === "login" && (
          <>
            <p style={sx.title}>Equipo identificado</p>
            <p style={sx.text}>
              Ingresá con tu cuenta para ver la ficha de este equipo.
            </p>
            {error && <div style={sx.error}>{error}</div>}
            <button style={sx.btn} onClick={entrar}>Ingresar con Google</button>
          </>
        )}

        {estado === "redirigiendo" && (
          <>
            <p style={sx.codigo}>{equipo?.codigo_lau}</p>
            <p style={sx.title}>{nombre}</p>
            <p style={sx.text}>Abriendo la ficha...</p>
            {destino && (
              <a href={destino} style={sx.link}>
                Si no se abre solo, tocá acá
              </a>
            )}
          </>
        )}

        {estado === "no_encontrado" && (
          <>
            <p style={sx.title}>Equipo no encontrado</p>
            <p style={sx.text}>
              Este código no corresponde a ningún equipo de tu empresa. Puede que
              la etiqueta sea vieja o de otra organización.
            </p>
            <button style={sx.btnAlt} onClick={() => (window.location.href = "/")}>
              Ir a ObraCore
            </button>
          </>
        )}

        {estado === "sin_acceso" && (
          <>
            <p style={sx.title}>Sin acceso a Equipos</p>
            <p style={sx.text}>
              Tu usuario no tiene permiso para ver la gestión de equipos. Pedíselo
              al administrador de tu empresa.
            </p>
            <button style={sx.btnAlt} onClick={() => (window.location.href = "/")}>
              Ir a ObraCore
            </button>
          </>
        )}

        {estado === "error" && (
          <>
            <p style={sx.title}>No se pudo abrir</p>
            <p style={sx.text}>{error}</p>
            <button style={sx.btnAlt} onClick={() => window.location.reload()}>
              Reintentar
            </button>
          </>
        )}

        <div style={sx.footer}>
          <span style={{ fontSize: 11, color: "#94a3b8" }}>
            Plataforma de gestión integral
          </span>
        </div>
      </div>
    </div>
  );
}

const sx = {
  container: {
    minHeight: "100vh", display: "flex", alignItems: "center",
    justifyContent: "center", background: "#f8f9fa", fontFamily: FONT, padding: 16,
  },
  card: {
    background: "white", borderRadius: 16, padding: "40px 32px", width: "100%",
    maxWidth: 400, boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
    textAlign: "center", borderTop: "3px solid #E03131",
  },
  logoArea: {
    display: "flex", flexDirection: "column", alignItems: "center",
    gap: 10, marginBottom: 24,
  },
  logoText: { fontSize: 24, letterSpacing: -0.5 },
  codigo: {
    fontFamily: "'Courier New', monospace", fontSize: 18, fontWeight: 800,
    color: "#E03131", marginBottom: 4,
  },
  title: { fontSize: 18, fontWeight: 700, color: "#1e293b", marginBottom: 8 },
  text: { fontSize: 14, color: "#64748b", lineHeight: 1.6, marginBottom: 16 },
  link: { fontSize: 13, color: "#E03131", fontWeight: 600 },
  btn: {
    width: "100%", padding: "12px", background: "#E03131", color: "white",
    border: "none", borderRadius: 8, fontSize: 15, fontWeight: 700,
    cursor: "pointer", fontFamily: FONT,
  },
  btnAlt: {
    width: "100%", padding: "11px", background: "transparent", color: "#4B5563",
    border: "0.5px solid #E5EAF2", borderRadius: 8, fontSize: 14,
    cursor: "pointer", fontFamily: FONT,
  },
  error: {
    fontSize: 13, color: "#dc2626", background: "#fef2f2", padding: "10px 16px",
    borderRadius: 8, marginBottom: 16, border: "1px solid #fecaca",
  },
  footer: { marginTop: 24, paddingTop: 16, borderTop: "1px solid #f1f5f9" },
};
